
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";

export type OrderStatus = "incomplete" | "in_progress" | "ready" | "completed" | "cancelled";
export type PaymentStatus = "pending" | "processing" | "completed" | "failed" | "refunded";

export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  incomplete: "Incomplete",
  in_progress: "In Progress",
  ready: "Ready",
  completed: "Completed",
  cancelled: "Cancelled"
};

export const PAYMENT_STATUS_LABELS: Record<PaymentStatus, string> = {
  pending: "Payment Pending",
  processing: "Processing Payment",
  completed: "Payment Complete",
  failed: "Payment Failed",
  refunded: "Refunded"
};

export const ORDER_STATUS_COLORS: Record<OrderStatus, string> = {
  incomplete: "bg-yellow-100 text-yellow-800",
  in_progress: "bg-blue-100 text-blue-800",
  ready: "bg-green-100 text-green-800",
  completed: "bg-purple-100 text-purple-800",
  cancelled: "bg-red-100 text-red-800"
};

export const PAYMENT_STATUS_COLORS: Record<PaymentStatus, string> = {
  pending: "bg-gray-100 text-gray-800",
  processing: "bg-blue-100 text-blue-800",
  completed: "bg-green-100 text-green-800",
  failed: "bg-red-100 text-red-800",
  refunded: "bg-orange-100 text-orange-800"
};

// Function to send notification when order status changes
const sendOrderNotification = async (orderId: string, type: 'confirmation' | 'status_update' | 'ready') => {
  try {
    console.log(`Sending ${type} notification for order ${orderId}`);
    
    const { data, error } = await supabase.functions.invoke('send-order-confirmation', {
      body: { orderId, type }
    });
    
    if (error) {
      console.error(`Error sending ${type} notification:`, error);
      return false;
    }
    
    console.log(`${type} notification sent successfully:`, data);
    return true;
  } catch (error) {
    console.error(`Exception sending ${type} notification:`, error);
    return false;
  }
};

export const updateOrderStatus = async (orderId: string, newStatus: OrderStatus) => {
  try {
    console.log(`Updating order ${orderId} status to ${newStatus}`);
    
    // Use direct database update instead of edge function
    // First, get current order to check permissions
    const { data: orderData, error: orderError } = await supabase
      .from("orders")
      .select("status, chef_id, payment_status")
      .eq("id", orderId)
      .single();
    
    if (orderError) {
      throw new Error("Order not found or you don't have permission to update it");
    }
    
    // Check if current user is the chef of this order
    const { data: { user } } = await supabase.auth.getUser();
    if (!user || orderData.chef_id !== user.id) {
      throw new Error("You don't have permission to update this order");
    }
    
    // Verify payment is completed before allowing status changes
    if (orderData.payment_status !== 'completed' && newStatus !== 'cancelled') {
      throw new Error("Cannot update order status until payment is completed");
    }
    
    // Prepare update data
    const updateData: Record<string, any> = {
      status: newStatus,
      updated_at: new Date().toISOString()
    };
    
    // If status is being changed to 'ready', set ready_at and ready_by
    if (newStatus === 'ready' && orderData.status !== 'ready') {
      updateData.ready_at = new Date().toISOString();
      updateData.ready_by = user.id;
    }
    
    // Update the order directly
    const { data: updatedOrder, error: updateError } = await supabase
      .from("orders")
      .update(updateData)
      .eq("id", orderId)
      .select()
      .single();
    
    if (updateError) {
      throw new Error(updateError.message || "Failed to update order status");
    }
    
    // Send notification if the order is now marked as ready
    if (newStatus === 'ready' && orderData.status !== 'ready') {
      // Trigger the ready notification
      const notificationSent = await sendOrderNotification(orderId, 'ready');
      if (!notificationSent) {
        console.warn("Ready notification could not be sent, but order status was updated");
      }
    } else if (newStatus !== orderData.status) {
      // Send a general status update notification for other status changes
      await sendOrderNotification(orderId, 'status_update');
    }
    
    toast({
      title: "Status Updated",
      description: `Order status changed to ${ORDER_STATUS_LABELS[newStatus]}`,
    });
    
    return updatedOrder;
  } catch (error: any) {
    console.error('Error updating order status:', error);
    toast({
      title: "Update Failed",
      description: error.message || "Could not update order status",
      variant: "destructive",
    });
    throw error;
  }
};

export const updatePaymentStatus = async (orderId: string, newStatus: PaymentStatus) => {
  try {
    console.log(`Updating order ${orderId} payment status to ${newStatus}`);
    
    // Get current order to check permissions
    const { data: orderData, error: orderError } = await supabase
      .from("orders")
      .select("payment_status, chef_id")
      .eq("id", orderId)
      .single();
    
    if (orderError) {
      throw new Error("Order not found or you don't have permission to update it");
    }
    
    // Check if current user is admin (this would need an admin check helper)
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error("You must be authenticated to perform this action");
    }
    
    // Prepare update data
    const updateData: Record<string, any> = {
      payment_status: newStatus,
      updated_at: new Date().toISOString()
    };
    
    // If payment status is being set to completed, set payment_completed_at
    if (newStatus === 'completed' && orderData.payment_status !== 'completed') {
      updateData['payment_completed_at'] = new Date().toISOString();
    }
    
    // Update the order directly
    const { data: updatedOrder, error: updateError } = await supabase
      .from("orders")
      .update(updateData)
      .eq("id", orderId)
      .select()
      .single();
    
    if (updateError) {
      throw new Error(updateError.message || "Failed to update payment status");
    }
    
    toast({
      title: "Payment Status Updated",
      description: `Payment status changed to ${PAYMENT_STATUS_LABELS[newStatus]}`,
    });
    
    return updatedOrder;
  } catch (error: any) {
    console.error('Error updating payment status:', error);
    toast({
      title: "Update Failed",
      description: error.message || "Could not update payment status",
      variant: "destructive",
    });
    throw error;
  }
};

export const canTransitionTo = (currentStatus: OrderStatus, newStatus: OrderStatus): boolean => {
  // Define allowed transitions
  const allowedTransitions: Record<OrderStatus, OrderStatus[]> = {
    incomplete: ["in_progress", "cancelled"],
    in_progress: ["ready", "cancelled"],
    ready: ["completed", "cancelled"],
    completed: [], // Terminal state
    cancelled: [] // Terminal state
  };

  return allowedTransitions[currentStatus]?.includes(newStatus) || false;
};
